/**
 * Design System Colors
 * CSS variables in App.css are the single source of truth.
 * This file provides utilities to access those colors in JavaScript/TypeScript.
 */

/**
 * Get the computed hex value of a CSS color variable.
 * Use this when you need the actual color value (e.g., for Ant Design theme, canvas, etc.)
 *
 * @example
 * const primaryColor = getColor('primary'); // returns '#0078d4'
 */
export const getColor = (name: string): string => {
    if (typeof window === 'undefined') {
        return '';
    }
    return getComputedStyle(document.documentElement).getPropertyValue(`--color-${name}`).trim();
};

/**
 * COLORS object using CSS variable references.
 * Use for inline styles in React components.
 *
 * @example
 * <div style={{ backgroundColor: COLORS.successBg }}>Success</div>
 */
export const COLORS = {
    // Primary Brand Colors
    primary: 'var(--color-primary)',
    primaryHover: 'var(--color-primary-hover)',
    skyBlueLight: 'var(--color-sky-blue-light)',
    skyBlueMedium: 'var(--color-sky-blue-medium)',
    skyBlueDark: 'var(--color-sky-blue-dark)',
    purpleLight: 'var(--color-purple-light)',
    purpleMedium: 'var(--color-purple-medium)',

    // Status Colors
    success: 'var(--color-success)',
    successBg: 'var(--color-success-bg)',
    successText: 'var(--color-success-text)',
    error: 'var(--color-error)',
    errorBg: 'var(--color-error-bg)',
    errorText: 'var(--color-error-text)',
    warning: 'var(--color-warning)',
    warningBg: 'var(--color-warning-bg)',
    warningText: 'var(--color-warning-text)',
    info: 'var(--color-info)',
    infoBg: 'var(--color-info-bg)',
    infoText: 'var(--color-info-text)',

    // Table Colors
    tableHeaderText: 'var(--color-table-text)',
    tableText: 'var(--color-table-text)',

    // Text Colors
    textPrimary: 'var(--color-text-primary)',
    textSecondary: 'var(--color-text-secondary)',
    textTertiary: 'var(--color-text-tertiary)',
    textDisabled: 'var(--color-text-disabled)',
    textPlaceholder: 'var(--color-text-disabled)',
    textTooltip: 'var(--color-text-tooltip)',

    headingPrimary: 'var(--color-text-primary)',

    // Background Colors
    bgContainer: 'var(--color-bg-container)',
    bgLayout: 'var(--color-bg-layout)',
    bgHover: 'var(--color-bg-hover)',
    bgSecondary: 'var(--color-bg-secondary)',
    menuBgHover: 'var(--color-menu-bg-hover)',
    tableRowHover: 'var(--color-table-row-hover)',
    tableHeaderBg: 'var(--color-table-header-bg)',

    // Border Colors
    border: 'var(--color-border)',
    borderLight: 'var(--color-border-light)',
    tableBorder: 'var(--color-table-border)',
} as const;

export type ColorKey = keyof typeof COLORS;
